import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog, faSignOutAlt, faTrash, faUser } from '@fortawesome/free-solid-svg-icons';

import {
  hasRoles,
  mergeRoles,
  CREATE_STUDY_ROLES,
  VIEW_STUDY_ROLES,
  EDIT_STUDY_ROLES,
  CREATE_FORM_ROLES,
  VIEW_FORM_ROLES,
  EDIT_FORM_ROLES,
  CREATE_PARTICIPANT_ROLES,
  VIEW_PARTICIPANT_ROLES,
  EDIT_PARTICIPANT_ROLES,
  CREATE_CLIENT_ROLES,
  VIEW_CLIENT_ROLES,
  EDIT_CLIENT_ROLES,
  CREATE_PARTICIPANT_REVIEW_ROLES,
  VIEW_PARTICIPANT_REVIEW_ROLES,
  EDIT_PARTICIPANT_REVIEW_ROLES,
  CREATE_CAPA_REVIEW_ROLES,
  VIEW_CAPA_REVIEW_ROLES,
  EDIT_CAPA_REVIEW_ROLES,
  VIEW_REPORTS_ROLES,
  VIEW_USERS_ROLES,
  EDIT_USER_SITES_ROLES,
  VIEW_FACILITIES_ROLES,
  EDIT_FACILITIES_ROLES,
  VIEW_SECURITY_QUESTION_ROLES,
  EDIT_SECURITY_QUESTION_ROLES,
  VIEW_QUERY_COMMENTS_ROLES,
  EDIT_QUERY_COMMENTS_ROLES,
  VIEW_NOTIFICATION_SETTINGS_ROLES,
  EDIT_NOTIFICATION_SETTINGS_ROLES,
  CLINEASE_ADMINISTRATOR,
  VIEW_QUALITY_CONTROL_DASHBOARD_ROLES,
  VIEW_DATA_MANAGEMENT_DASHBOARD_ROLES,
} from '../../../util/RoleUtil';

import { retrieveLoggedInSystemUser } from '../../../services/StaffMemberService';

import { SideMenuSection } from './side-menu-section/SideMenuSection';
import DateTime from './date-time/DateTime';

import './SideMenu.css';

const dashboardRoles = mergeRoles(VIEW_QUALITY_CONTROL_DASHBOARD_ROLES, VIEW_DATA_MANAGEMENT_DASHBOARD_ROLES);
const studyRoles = mergeRoles(CREATE_STUDY_ROLES, VIEW_STUDY_ROLES, EDIT_STUDY_ROLES);
const formRoles = mergeRoles(CREATE_FORM_ROLES, VIEW_FORM_ROLES, EDIT_FORM_ROLES);
const participantRoles = mergeRoles(CREATE_PARTICIPANT_ROLES, VIEW_PARTICIPANT_ROLES, EDIT_PARTICIPANT_ROLES);
const clientRoles = mergeRoles(CREATE_CLIENT_ROLES, VIEW_CLIENT_ROLES, EDIT_CLIENT_ROLES);
const participantReviewRoles = mergeRoles(
  CREATE_PARTICIPANT_REVIEW_ROLES,
  VIEW_PARTICIPANT_REVIEW_ROLES,
  EDIT_PARTICIPANT_REVIEW_ROLES
);
const capaReviewRoles = mergeRoles(CREATE_CAPA_REVIEW_ROLES, VIEW_CAPA_REVIEW_ROLES, EDIT_CAPA_REVIEW_ROLES);
const userRoles = mergeRoles(VIEW_USERS_ROLES, EDIT_USER_SITES_ROLES);
const facilityRoles = mergeRoles(VIEW_FACILITIES_ROLES, EDIT_FACILITIES_ROLES);
const securityQuestionRoles = mergeRoles(VIEW_SECURITY_QUESTION_ROLES, EDIT_SECURITY_QUESTION_ROLES);
const queryCommentRoles = mergeRoles(VIEW_QUERY_COMMENTS_ROLES, EDIT_QUERY_COMMENTS_ROLES);
const notificationSettingsRoles = mergeRoles(VIEW_NOTIFICATION_SETTINGS_ROLES, EDIT_NOTIFICATION_SETTINGS_ROLES);

export const SideMenu = ({ navigateTo, context, setContext, keycloak }) => {
  const [dashboardClosed, setDashboardClosed] = useState(true);
  const [studiesAndTrialsClosed, setStudiesAndTrialsClosed] = useState(true);
  const [clientsClosed, setClientsClosed] = useState(true);
  const [queriesClosed, setQueriesClosed] = useState(true);
  const [reportsClosed, setReportsClosed] = useState(true);
  const [adminClosed, setAdminClosed] = useState(true);
  const [showDropdown, setShowDropdown] = useState(false);

  const toggleDropDown = () => {
    setShowDropdown((prevState) => !prevState);
  };

  const studiesAndTrialsMenuLinks = [
    {
      name: 'Study/Trial Administration',
      screenKey: 'studyTrialAdmin',
      visible: hasRoles(context, studyRoles),
    },
    {
      name: 'Form Administration',
      screenKey: 'formAdmin',
      visible: hasRoles(context, formRoles),
    },
    {
      name: 'Participants',
      screenKey: 'participants',
      visible: hasRoles(context, participantRoles),
    },
  ];

  const queriesMenuLinks = [
    {
      name: 'Participant Reviews',
      screenKey: 'participantReviewsTable',
      visible: hasRoles(context, participantReviewRoles),
    },
    {
      name: 'CAPA Reviews',
      screenKey: 'capaReviewsTable',
      visible: hasRoles(context, capaReviewRoles),
    },
  ];

  const canViewReports = hasRoles(context, VIEW_REPORTS_ROLES);
  const reportsMenuLinks = [
    {
      name: 'Quality Control',
      screenKey: 'qualityControl',
      visible: canViewReports,
    },
    {
      name: 'Data Management',
      screenKey: 'dataManagement',
      visible: canViewReports,
    },
  ];

  const adminMenuLinks = [
    {
      name: 'Users',
      screenKey: 'usersTable',
      visible: hasRoles(context, userRoles),
    },
    {
      name: 'Facilities',
      screenKey: 'facilitiesManagement',
      visible: hasRoles(context, facilityRoles),
    },
    {
      name: 'Client Security Questions',
      screenKey: 'clientSecurityQuestions',
      visible: hasRoles(context, securityQuestionRoles),
    },
    {
      name: 'Generic Query Comments',
      screenKey: 'genericQueryComments',
      visible: hasRoles(context, queryCommentRoles),
    },
    {
      name: 'Notification Settings',
      screenKey: 'capaNotificationSettings',
      visible: hasRoles(context, notificationSettingsRoles),
    },
  ];

  const toggleClosed = (title) => {
    switch (title) {
      case 'Dashboard':
        navigateTo('dashboard');
        setDashboardClosed(!dashboardClosed);
        setStudiesAndTrialsClosed(true);
        setClientsClosed(true);
        setQueriesClosed(true);
        setReportsClosed(true);
        setAdminClosed(true);
        setContext((prevContext) => ({
          ...prevContext,
          activeSection: undefined,
        }));
        break;
      case 'Studies and Trials':
        setDashboardClosed(true);
        setStudiesAndTrialsClosed(!studiesAndTrialsClosed);
        setClientsClosed(true);
        setQueriesClosed(true);
        setReportsClosed(true);
        setAdminClosed(true);
        break;
      case 'Clients':
        navigateTo('clientsSearch');
        setDashboardClosed(true);
        setStudiesAndTrialsClosed(true);
        setClientsClosed(!clientsClosed);
        setQueriesClosed(true);
        setReportsClosed(true);
        setAdminClosed(true);
        setContext((prevContext) => ({
          ...prevContext,
          activeSection: undefined,
        }));
        break;
      case 'Queries':
        setDashboardClosed(true);
        setStudiesAndTrialsClosed(true);
        setClientsClosed(true);
        setQueriesClosed(!queriesClosed);
        setReportsClosed(true);
        setAdminClosed(true);
        break;
      case 'Reports':
        setDashboardClosed(true);
        setStudiesAndTrialsClosed(true);
        setClientsClosed(true);
        setQueriesClosed(true);
        setReportsClosed(!reportsClosed);
        setAdminClosed(true);
        break;
      default:
        setDashboardClosed(true);
        setStudiesAndTrialsClosed(true);
        setClientsClosed(true);
        setQueriesClosed(true);
        setReportsClosed(true);
        setAdminClosed(!adminClosed);
        break;
    }
  };

  const [menuActive, setMenuActive] = useState(false);

  const handleMenuClick = () => {
    setMenuActive(!menuActive);
  };

  const handleLogOutClick = () => {
    localStorage.removeItem('token');
    keycloak.logout();
  };

  const handleSettingsClick = () => {
    setShowDropdown(false);
    setContext((prevState) => ({
      ...prevState,
      activeSection: undefined,
    }));
    navigateTo('settings');
  };

  const handleClearSessionClick = async () => {
    setShowDropdown(false);
    const username = context.username;
    const roles = context.roles;
    const [data, error] = await retrieveLoggedInSystemUser(username);
    if (data) {
      if (data.responseStatus === 'Success') {
        let currentScreen = 'dashboard';
        let activeSection = 'Dashboard';

        if (roles.includes(CLINEASE_ADMINISTRATOR)) {
          currentScreen = 'usersTable';
          activeSection = 'Administration';
        }

        setContext({
          viewStack: [],
          breadcrumbs: [],
          username,
          roles,
          loggedInUser: data.systemUserVO,
          currentScreen,
          activeSection,
        });
      } else {
        console.error(data.responseMessage);
      }
    } else {
      console.error(error);
    }
  };

  return (
    <div className="side-menu-container" onClick={handleMenuClick}>
      {hasRoles(context, dashboardRoles) && (
        <div
          className={
            'side-menu-section-container ' + (dashboardClosed ? 'side-menu-section-closed' : 'side-menu-section-open')
          }
        >
          <p className="side-menu-section-text" onClick={() => toggleClosed('Dashboard')}>
            Dashboard
          </p>
        </div>
      )}
      {hasRoles(context, mergeRoles(studyRoles, formRoles, participantRoles)) && (
        <SideMenuSection
          title="Studies and Trials"
          links={studiesAndTrialsMenuLinks}
          activeSection={context.activeSection}
          closed={studiesAndTrialsClosed}
          toggleClosed={toggleClosed}
          navigateTo={navigateTo}
          setContext={setContext}
        />
      )}
      {hasRoles(context, mergeRoles(clientRoles)) && (
        <div
          className={
            'side-menu-section-container ' + (clientsClosed ? 'side-menu-section-closed' : 'side-menu-section-open')
          }
        >
          <p className="side-menu-section-text" onClick={() => toggleClosed('Clients')}>
            Clients
          </p>
        </div>
      )}
      {hasRoles(context, mergeRoles(participantReviewRoles, capaReviewRoles)) && (
        <SideMenuSection
          title="Queries"
          links={queriesMenuLinks}
          activeSection={context.activeSection}
          closed={queriesClosed}
          toggleClosed={toggleClosed}
          navigateTo={navigateTo}
          setContext={setContext}
        />
      )}
      {hasRoles(context, VIEW_REPORTS_ROLES) && (
        <SideMenuSection
          title="Reports"
          links={reportsMenuLinks}
          activeSection={context.activeSection}
          closed={reportsClosed}
          toggleClosed={toggleClosed}
          navigateTo={navigateTo}
          setContext={setContext}
        />
      )}
      {hasRoles(
        context,
        mergeRoles(userRoles, facilityRoles, securityQuestionRoles, queryCommentRoles, notificationSettingsRoles)
      ) && (
        <SideMenuSection
          title="Administration"
          links={adminMenuLinks}
          activeSection={context.activeSection}
          closed={adminClosed}
          toggleClosed={toggleClosed}
          navigateTo={navigateTo}
          setContext={setContext}
        />
      )}

      <div className="side-menu-dropdown-menu-container">
        <div className="side-menu-username-time-container">
          <p className="bold">{context.username}</p>
          <DateTime />
        </div>
        <div className="side-menu-icon-container">
          <FontAwesomeIcon
            data-testid="side-menu-button"
            className="side-menu-user-icon"
            icon={faUser}
            onClick={toggleDropDown}
          />
        </div>
        {showDropdown && (
          <div className="side-menu-dropdown-menu">
            <div className="side-menu-dropdown-menu-item" onClick={handleSettingsClick}>
              <div className="side-menu-dropdown-menu-item-icon">
                <FontAwesomeIcon className="side-menu-dropdown-menu-item-icon" icon={faCog} />
              </div>
              <p>Settings</p>
            </div>
            <div className="side-menu-dropdown-menu-item" onClick={handleClearSessionClick}>
              <div className="side-menu-dropdown-menu-item-icon">
                <FontAwesomeIcon className="side-menu-dropdown-menu-item-icon" icon={faTrash} />
              </div>
              <p>Clear Session</p>
            </div>
            <div className="side-menu-dropdown-menu-item" onClick={handleLogOutClick}>
              <div className="side-menu-dropdown-menu-item-icon">
                <FontAwesomeIcon className="side-menu-dropdown-menu-item-icon" icon={faSignOutAlt} />
              </div>
              <p>Sign Out</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
